import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Switch } from '../components/ui/Switch';
import { MockBadge } from '../components/MockBadge';
import { BrowseTrackingCard } from '../components/BrowseTrackingCard';
import { ImageSourceInput } from '../components/ImageSourceInput';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ApiError,
  type MailingList,
  type PopupFont,
  type PopupLanguage,
  type PopupSettings,
  type PopupTexts,
  type PopupTargetMode,
  type PopupTrigger,
} from '../types/api';

/** Same limits as the server (core `POPUP_LIMITS`); the server checks them again. */
const LIMITS = {
  headline: 120,
  body: 400,
  buttonText: 40,
  successText: 200,
  delaySeconds: { min: 1, max: 300 },
  scrollPercent: { min: 5, max: 100 },
  frequencyDays: { min: 1, max: 365 },
  cornerRadius: { min: 0, max: 24 },
  couponCode: 40,
  targetUrlPart: 200,
} as const;

/** English is the main text and the fallback; the shop's other languages can have their own. */
const LANGUAGES: { value: 'en' | PopupLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'da', label: 'Danish' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
];
type Lang = (typeof LANGUAGES)[number]['value'];
type TextKey = keyof PopupTexts;
const TEXT_KEYS: TextKey[] = ['headline', 'body', 'buttonText', 'successText'];

/** The popup's own words in the preview (the shop script has the same ones). */
const WORDS: Record<Lang, { email: string; name: string; code: string; copy: string }> = {
  en: { email: 'Email address', name: 'First name', code: 'Your discount code', copy: 'Copy' },
  da: { email: 'E-mailadresse', name: 'Fornavn', code: 'Din rabatkode', copy: 'Kopiér' },
  de: { email: 'E-Mail-Adresse', name: 'Vorname', code: 'Dein Rabattcode', copy: 'Kopieren' },
  fr: { email: 'Adresse e-mail', name: 'Prénom', code: 'Votre code promo', copy: 'Copier' },
  it: { email: 'Indirizzo email', name: 'Nome', code: 'Il tuo codice sconto', copy: 'Copia' },
  nl: { email: 'E-mailadres', name: 'Voornaam', code: 'Je kortingscode', copy: 'Kopiëren' },
};

const PRESETS: {
  label: string;
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
}[] = [
  {
    label: 'Shop colours',
    backgroundColor: '#ffffff',
    textColor: '#243a53',
    buttonColor: '#208233',
  },
  { label: 'Dark navy', backgroundColor: '#243a53', textColor: '#ffffff', buttonColor: '#208233' },
];

const FONTS: { value: PopupFont; label: string; css: string }[] = [
  { value: 'poppins', label: 'Poppins (shop headings)', css: 'Poppins, system-ui, sans-serif' },
  { value: 'roboto', label: 'Roboto (shop text)', css: 'Roboto, system-ui, sans-serif' },
  { value: 'system', label: 'Device font', css: 'system-ui, sans-serif' },
];

const HEX = /^#[0-9a-fA-F]{6}$/;

function luminance(hex: string): number {
  const channel = (i: number) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

function contrast(a: string, b: string): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** Black or white on the button, the same rule as the shop script (edge `readableTextColor`). */
function buttonTextColor(hex: string): string {
  return contrast(hex, '#ffffff') >= contrast(hex, '#111111') ? '#ffffff' : '#111111';
}

const TRIGGERS: { value: PopupTrigger; label: string; hint: string }[] = [
  {
    value: 'delay',
    label: 'After a delay',
    hint: 'Shows after the visitor has been on the page for a while.',
  },
  {
    value: 'scroll',
    label: 'After scrolling',
    hint: 'Shows when the visitor has scrolled part of the way down.',
  },
  {
    value: 'exit_intent',
    label: 'Exit intent',
    hint: 'Shows when the mouse leaves the page. Does not fire on phones or tablets.',
  },
];

type Draft = Omit<PopupSettings, 'updatedAt' | 'embedCode'>;

function toDraft(s: PopupSettings): Draft {
  return {
    enabled: s.enabled,
    headline: s.headline,
    body: s.body,
    buttonText: s.buttonText,
    successText: s.successText,
    listId: s.listId,
    trigger: s.trigger,
    delaySeconds: s.delaySeconds,
    scrollPercent: s.scrollPercent,
    frequencyDays: s.frequencyDays,
    showOnMobile: s.showOnMobile,
    imageUrl: s.imageUrl,
    backgroundColor: s.backgroundColor,
    textColor: s.textColor,
    buttonColor: s.buttonColor,
    cornerRadius: s.cornerRadius,
    headingFont: s.headingFont,
    dimBackground: s.dimBackground,
    showNameField: s.showNameField,
    couponCode: s.couponCode,
    translations: s.translations,
    targetMode: s.targetMode,
    targetUrlPart: s.targetUrlPart,
  };
}

/** The text shown in `lang`: its own when written, else the English one. */
function textFor(d: Draft, lang: Lang, key: TextKey): string {
  if (lang === 'en') return d[key];
  const own = d.translations[lang]?.[key];
  return own && own.trim() !== '' ? own : d[key];
}

function missingIn(d: Draft, lang: Lang): boolean {
  if (lang === 'en') return false;
  return TEXT_KEYS.some((k) => !(d.translations[lang]?.[k] ?? '').trim());
}

function inRange(value: number, range: { min: number; max: number }): boolean {
  return Number.isInteger(value) && value >= range.min && value <= range.max;
}

function validate(d: Draft): string | null {
  if (d.headline.trim() === '') return 'Enter a headline.';
  if (d.body.trim() === '') return 'Enter a text.';
  if (d.buttonText.trim() === '') return 'Enter the button text.';
  if (d.successText.trim() === '') return 'Enter the thank-you text.';
  if (!inRange(d.delaySeconds, LIMITS.delaySeconds)) return 'The delay must be 1 to 300 seconds.';
  if (!inRange(d.scrollPercent, LIMITS.scrollPercent)) {
    return 'The scroll depth must be 5 to 100 percent.';
  }
  if (!inRange(d.frequencyDays, LIMITS.frequencyDays)) {
    return 'Days between showings must be 1 to 365.';
  }
  if (![d.backgroundColor, d.textColor, d.buttonColor].every((c) => HEX.test(c))) {
    return 'Colours must look like #208233.';
  }
  if (d.couponCode !== null && !/^[A-Za-z0-9_-]{1,40}$/.test(d.couponCode)) {
    return 'The discount code can only have letters, digits, - and _ (at most 40).';
  }
  if (d.targetMode === 'url' && (d.targetUrlPart ?? '').trim() === '') {
    return 'Enter the text the page address must contain.';
  }
  return null;
}

function NumberField({
  id,
  label,
  value,
  onChange,
  disabled,
  suffix,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (n: number) => void;
  disabled: boolean;
  suffix: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          className="max-w-[110px]"
          value={Number.isNaN(value) ? '' : value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? Number.NaN : Number(e.target.value))}
        />
        <span className="text-sm text-muted">{suffix}</span>
      </div>
    </div>
  );
}

/**
 * Draws its child at a fixed width (a phone or a computer screen) and
 * scales it down to the column, so the preview keeps the shop's proportions.
 */
function FitWidth({
  label,
  width,
  children,
}: {
  label: string;
  width: number;
  children: ReactNode;
}) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ scale: 1, height: 0 });
  useLayoutEffect(() => {
    const o = outer.current;
    const i = inner.current;
    if (!o || !i || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const scale = Math.min(1, o.clientWidth / width);
      setBox({ scale, height: i.offsetHeight * scale });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(o);
    ro.observe(i);
    return () => ro.disconnect();
  }, [width]);
  return (
    <div ref={outer} aria-label={label} style={{ height: box.height || undefined }}>
      <div
        ref={inner}
        style={{ width, transform: `scale(${box.scale})`, transformOrigin: 'top left' }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A picture of the popup as the shop draws it (image left, text right; a
 * bottom sheet on phones), in the chosen colours and language. React
 * escapes the text, so it is only text.
 */
function Preview({
  draft,
  lang,
  device,
  view,
}: {
  draft: Draft;
  lang: Lang;
  device: 'desktop' | 'phone';
  view: 'form' | 'done';
}) {
  const phone = device === 'phone';
  const r = draft.cornerRadius;
  const inner = Math.round(r * 0.66);
  const head = FONTS.find((f) => f.value === draft.headingFont)?.css ?? FONTS[0]!.css;
  const words = WORDS[lang];
  const btnText = buttonTextColor(draft.buttonColor);
  const showImage = draft.imageUrl !== null;
  return (
    <FitWidth label="Popup preview" width={phone ? 360 : 720}>
      <div
        className="relative overflow-hidden rounded-xl border border-zinc-200"
        style={{
          background: draft.dimBackground ? 'rgba(16,24,36,.55)' : '#eef1f5',
          minHeight: phone ? 600 : 520,
          display: 'flex',
          alignItems: phone ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: phone ? 0 : 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: phone || !showImage ? 'column' : 'row',
            width: '100%',
            maxWidth: phone ? undefined : showImage ? 600 : 400,
            background: draft.backgroundColor,
            color: draft.textColor,
            borderRadius: phone ? `${r}px ${r}px 0 0` : r,
            overflow: 'hidden',
            boxShadow: '0 24px 70px rgba(10,20,35,.32)',
            fontFamily: draft.headingFont === 'system' ? 'system-ui, sans-serif' : FONTS[1]!.css,
          }}
        >
          {showImage ? (
            <img
              src={draft.imageUrl ?? undefined}
              alt=""
              style={{
                width: phone ? '100%' : '42%',
                height: phone ? 120 : undefined,
                minHeight: phone ? undefined : 260,
                objectFit: 'cover',
              }}
            />
          ) : null}
          <div style={{ flex: 1, padding: phone ? '20px 18px 24px' : '28px 26px 22px' }}>
            {view === 'form' ? (
              <>
                <div
                  style={{
                    fontFamily: head,
                    fontWeight: 800,
                    fontSize: phone ? 20 : 23,
                    lineHeight: 1.15,
                    marginBottom: 8,
                  }}
                >
                  {textFor(draft, lang, 'headline')}
                </div>
                <p className="m-0 mb-3 text-sm whitespace-pre-wrap" style={{ opacity: 0.88 }}>
                  {textFor(draft, lang, 'body')}
                </p>
                {draft.showNameField ? (
                  <div
                    className="mb-2 flex h-[40px] items-center px-3 text-sm"
                    style={{
                      background: '#fff',
                      color: '#6b7280',
                      border: '1px solid #8a94a3',
                      borderRadius: inner,
                    }}
                  >
                    {words.name}
                  </div>
                ) : null}
                <div
                  className="mb-2 flex h-[40px] items-center px-3 text-sm"
                  style={{
                    background: '#fff',
                    color: '#6b7280',
                    border: '1px solid #8a94a3',
                    borderRadius: inner,
                  }}
                >
                  {words.email}
                </div>
                <div
                  className="flex h-[42px] items-center justify-center text-sm font-bold"
                  style={{ background: draft.buttonColor, color: btnText, borderRadius: inner }}
                >
                  {textFor(draft, lang, 'buttonText')}
                </div>
                <p className="m-0 mt-2 text-[11px] leading-snug" style={{ opacity: 0.7 }}>
                  The consent text from NP-Flow is shown here, in the page&apos;s language.
                </p>
              </>
            ) : (
              <>
                <div style={{ fontFamily: head, fontWeight: 800, fontSize: 20, lineHeight: 1.25 }}>
                  {textFor(draft, lang, 'successText')}
                </div>
                {draft.couponCode ? (
                  <>
                    <p className="m-0 mt-3 mb-1 text-sm">{words.code}</p>
                    <div
                      className="flex items-center justify-between gap-3 px-3 py-2 font-mono text-base font-bold tracking-wider"
                      style={{ border: `2px dashed ${draft.buttonColor}`, borderRadius: inner }}
                    >
                      {draft.couponCode}
                      <span
                        className="px-2 py-1 text-xs font-semibold tracking-normal"
                        style={{
                          background: draft.buttonColor,
                          color: btnText,
                          borderRadius: Math.round(r * 0.5),
                        }}
                      >
                        {words.copy}
                      </span>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </FitWidth>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; hint?: string | undefined }[];
  onChange: (v: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          title={o.hint}
          onClick={() => onChange(o.value)}
          className={
            'rounded-[9px] border px-3 py-1.5 text-sm ' +
            (value === o.value ? 'border-accent bg-accent/10 font-semibold' : 'border-zinc-300')
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ColorField({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-hidden="true"
          tabIndex={-1}
          className="h-[38px] w-[44px] cursor-pointer rounded-[9px] border border-zinc-300 bg-card p-0.5"
          value={HEX.test(value) ? value : '#000000'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <Input
          id={id}
          className="max-w-[110px] font-mono"
          value={value}
          maxLength={7}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.trim())}
        />
      </div>
    </div>
  );
}

/**
 * Signup forms (migration 0049): the sitewide signup popup. The shop pastes
 * one script tag into its theme; the edge serves the script with these
 * settings. Everyone can read; editors and admins can save.
 */
export function SignupFormsPage() {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState<Lang>('en');
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const [view, setView] = useState<'form' | 'done'>('form');

  const settings = useQuery({
    queryKey: ['popup-settings'],
    queryFn: () => apiFetch<PopupSettings>('/settings/popup'),
  });
  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: () => apiFetch<{ items: MailingList[] }>('/lists'),
  });

  useEffect(() => {
    if (settings.data) setDraft((current) => current ?? toDraft(settings.data));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (input: Draft) =>
      apiFetch<PopupSettings>('/settings/popup', { method: 'PUT', body: input }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['popup-settings'], saved);
      setDraft(toDraft(saved));
    },
  });

  const saveError =
    save.error instanceof ApiError
      ? (save.error.body.message ?? 'Could not be saved. Try again.')
      : save.isError
        ? 'Could not be saved. Try again.'
        : null;

  const chooseableLists = (lists.data?.items ?? []).filter(
    (l) => !l.archived && l.isPublic && !l.isDefaultSignup,
  );
  const problem = draft ? validate(draft) : null;
  const readOnly = !isEditor;
  const embedCode = settings.data?.embedCode ?? null;

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    save.reset();
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  /** Edits one text in the chosen language: English is the main field, the rest are translations. */
  function setText(key: TextKey, value: string) {
    save.reset();
    setView(key === 'successText' ? 'done' : 'form');
    setDraft((d) => {
      if (!d) return d;
      if (lang === 'en') return { ...d, [key]: value };
      return {
        ...d,
        translations: { ...d.translations, [lang]: { ...d.translations[lang], [key]: value } },
      };
    });
  }

  function textValue(d: Draft, key: TextKey): string {
    return lang === 'en' ? d[key] : (d.translations[lang]?.[key] ?? '');
  }

  const fallbackHint = (key: TextKey) =>
    draft && lang !== 'en' ? `Empty: shows the English text, "${draft[key]}"` : undefined;
  const textContrast = draft ? contrast(draft.textColor, draft.backgroundColor) : 21;
  const colorsValid = draft
    ? [draft.backgroundColor, draft.textColor].every((c) => HEX.test(c))
    : false;

  async function copyEmbed() {
    if (!embedCode) return;
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Signup forms</h1>
        <MockBadge />
      </header>
      <p className="m-0 text-sm text-muted max-w-[640px]">
        A popup on the shop that asks visitors for their email address. It uses the same consent
        text, spam check and lists as the signup form. The browser remembers only when the popup was
        closed or used, never who the visitor is.
      </p>

      {readOnly ? (
        <Alert>You have read-only access. Contact an editor to change the popup.</Alert>
      ) : null}
      {settings.isLoading ? <div className="text-muted">Loading the popup…</div> : null}
      {settings.isError ? <Alert>The popup settings could not be loaded.</Alert> : null}

      {draft ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start">
          <Card className="p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col">
                <span className="text-sm font-semibold">Popup is on</span>
                <span className="text-xs text-muted">
                  Off means the script on the shop does nothing.
                </span>
              </div>
              <Switch
                label="Popup is on"
                checked={draft.enabled}
                disabled={readOnly}
                onChange={() => set('enabled', !draft.enabled)}
              />
            </div>

            <section className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="m-0 text-sm font-semibold">Texts</h2>
                <span className="text-xs text-muted">
                  The popup follows the language of the shop page. A language without its own text
                  shows the English one.
                </span>
              </div>
              <Segmented
                label="Language"
                value={lang}
                onChange={setLang}
                options={LANGUAGES.map((l) => ({
                  value: l.value,
                  label: missingIn(draft, l.value) ? `${l.label} ·` : l.label,
                  hint: missingIn(draft, l.value) ? 'Some texts fall back to English' : undefined,
                }))}
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pf-headline">Headline</Label>
                <Input
                  id="pf-headline"
                  value={textValue(draft, 'headline')}
                  placeholder={lang === 'en' ? undefined : draft.headline}
                  maxLength={LIMITS.headline}
                  disabled={readOnly}
                  title={fallbackHint('headline')}
                  onChange={(e) => setText('headline', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pf-body">Text</Label>
                <textarea
                  id="pf-body"
                  className="w-full min-h-[84px] rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
                  value={textValue(draft, 'body')}
                  placeholder={lang === 'en' ? undefined : draft.body}
                  maxLength={LIMITS.body}
                  disabled={readOnly}
                  onChange={(e) => setText('body', e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pf-button">Button text</Label>
                  <Input
                    id="pf-button"
                    value={textValue(draft, 'buttonText')}
                    placeholder={lang === 'en' ? undefined : draft.buttonText}
                    maxLength={LIMITS.buttonText}
                    disabled={readOnly}
                    onChange={(e) => setText('buttonText', e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pf-success">Thank-you text</Label>
                  <Input
                    id="pf-success"
                    value={textValue(draft, 'successText')}
                    placeholder={lang === 'en' ? undefined : draft.successText}
                    maxLength={LIMITS.successText}
                    disabled={readOnly}
                    onChange={(e) => setText('successText', e.target.value)}
                  />
                </div>
              </div>
              {lang !== 'en' && missingIn(draft, lang) ? (
                <span className="text-xs text-muted">
                  Empty fields show the English text (greyed out above).
                </span>
              ) : null}
            </section>

            <section className="flex flex-col gap-3 border-t border-zinc-200 pt-4">
              <h2 className="m-0 text-sm font-semibold">Design</h2>
              {readOnly ? null : (
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((p) => (
                    <Button
                      key={p.label}
                      variant="secondary"
                      onClick={() => {
                        set('backgroundColor', p.backgroundColor);
                        set('textColor', p.textColor);
                        set('buttonColor', p.buttonColor);
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-4">
                <ColorField
                  id="pf-bg"
                  label="Background"
                  value={draft.backgroundColor}
                  disabled={readOnly}
                  onChange={(v) => set('backgroundColor', v)}
                />
                <ColorField
                  id="pf-text-colour"
                  label="Text colour"
                  value={draft.textColor}
                  disabled={readOnly}
                  onChange={(v) => set('textColor', v)}
                />
                <ColorField
                  id="pf-button-colour"
                  label="Button colour"
                  value={draft.buttonColor}
                  disabled={readOnly}
                  onChange={(v) => set('buttonColor', v)}
                />
              </div>
              {colorsValid && textContrast < 4.5 ? (
                <Alert>
                  The text is hard to read on this background (contrast {textContrast.toFixed(1)}:1,
                  aim for at least 4.5:1).
                </Alert>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pf-radius">Corners: {draft.cornerRadius} px</Label>
                  <input
                    id="pf-radius"
                    type="range"
                    min={LIMITS.cornerRadius.min}
                    max={LIMITS.cornerRadius.max}
                    step={2}
                    value={draft.cornerRadius}
                    disabled={readOnly}
                    className="accent-[var(--color-accent)]"
                    onChange={(e) => set('cornerRadius', Number(e.target.value))}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pf-font">Headline font</Label>
                  <select
                    id="pf-font"
                    className="h-[38px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
                    value={draft.headingFont}
                    disabled={readOnly}
                    onChange={(e) => set('headingFont', e.target.value as PopupFont)}
                  >
                    {FONTS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {readOnly ? null : (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Image (optional)</span>
                  <span className="text-xs text-muted">
                    Shown on the left, or at the top on phones. Without one, the popup is text only.
                  </span>
                  <ImageSourceInput
                    id="pf-image"
                    label="Image (optional)"
                    value={draft.imageUrl ?? ''}
                    onChange={(next) => set('imageUrl', next.trim() === '' ? null : next.trim())}
                    variant="row"
                  />
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Dim the page behind the popup</span>
                  <span className="text-xs text-muted">A dark layer over the shop.</span>
                </div>
                <Switch
                  label="Dim the page behind the popup"
                  checked={draft.dimBackground}
                  disabled={readOnly}
                  onChange={() => set('dimBackground', !draft.dimBackground)}
                />
              </div>
            </section>

            <section className="flex flex-col gap-3 border-t border-zinc-200 pt-4">
              <h2 className="m-0 text-sm font-semibold">Form</h2>
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Ask for the first name</span>
                  <span className="text-xs text-muted">
                    Optional for the visitor. Email is always asked.
                  </span>
                </div>
                <Switch
                  label="Ask for the first name"
                  checked={draft.showNameField}
                  disabled={readOnly}
                  onChange={() => set('showNameField', !draft.showNameField)}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Show a discount code after signup</span>
                  <span className="text-xs text-muted">
                    Off until you decide. Everyone who signs up sees it straight away, also before
                    they confirm their email.
                  </span>
                </div>
                <Switch
                  label="Show a discount code after signup"
                  checked={draft.couponCode !== null}
                  disabled={readOnly}
                  onChange={() => {
                    set('couponCode', draft.couponCode === null ? '' : null);
                    setView('done');
                  }}
                />
              </div>
              {draft.couponCode !== null ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pf-coupon">Discount code</Label>
                  <Input
                    id="pf-coupon"
                    className="max-w-[240px] font-mono"
                    value={draft.couponCode}
                    maxLength={LIMITS.couponCode}
                    disabled={readOnly}
                    onChange={(e) => {
                      set('couponCode', e.target.value.trim());
                      setView('done');
                    }}
                  />
                  <span className="text-xs text-muted">
                    Must be a code that already exists in the shop (WooCommerce → Coupons).
                  </span>
                </div>
              ) : null}
            </section>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pf-list">List</Label>
              <select
                id="pf-list"
                className="h-[38px] max-w-[320px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
                value={draft.listId ?? ''}
                disabled={readOnly}
                onChange={(e) => set('listId', e.target.value === '' ? null : e.target.value)}
              >
                <option value="">Default signup list</option>
                {chooseableLists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
              <span className="text-xs text-muted">
                Only the default list and public lists can be used.
              </span>
            </div>

            <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5">
              <legend className="text-sm font-medium mb-1.5">When it shows</legend>
              {TRIGGERS.map((t) => (
                <label key={t.value} className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="pf-trigger"
                    className="mt-1"
                    value={t.value}
                    checked={draft.trigger === t.value}
                    disabled={readOnly}
                    onChange={() => set('trigger', t.value)}
                  />
                  <span>
                    {t.label}
                    <span className="block text-xs text-muted">{t.hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            <div className="flex flex-wrap gap-4">
              {draft.trigger === 'delay' ? (
                <NumberField
                  id="pf-delay"
                  label="Delay"
                  suffix="seconds"
                  value={draft.delaySeconds}
                  disabled={readOnly}
                  onChange={(n) => set('delaySeconds', n)}
                />
              ) : null}
              {draft.trigger === 'scroll' ? (
                <NumberField
                  id="pf-scroll"
                  label="Scroll depth"
                  suffix="percent"
                  value={draft.scrollPercent}
                  disabled={readOnly}
                  onChange={(n) => set('scrollPercent', n)}
                />
              ) : null}
              <NumberField
                id="pf-frequency"
                label="Show again after"
                suffix="days"
                value={draft.frequencyDays}
                disabled={readOnly}
                onChange={(n) => set('frequencyDays', n)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pf-target">Which pages</Label>
              <select
                id="pf-target"
                className="h-[38px] max-w-[320px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
                value={draft.targetMode}
                disabled={readOnly}
                onChange={(e) => set('targetMode', e.target.value as PopupTargetMode)}
              >
                <option value="all">Every page</option>
                <option value="url">Pages whose address contains…</option>
                <option value="product">Product pages</option>
                <option value="cart">The cart page</option>
              </select>
              {draft.targetMode === 'url' ? (
                <Input
                  id="pf-target-url"
                  aria-label="Text the page address must contain"
                  className="max-w-[320px]"
                  placeholder="/sale"
                  maxLength={LIMITS.targetUrlPart}
                  value={draft.targetUrlPart ?? ''}
                  disabled={readOnly}
                  onChange={(e) => set('targetUrlPart', e.target.value)}
                />
              ) : null}
              <span className="text-xs text-muted">
                Narrows where the popup may show. The popup itself stays off until you turn it on.
              </span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">Show on phones</span>
              <Switch
                label="Show on phones"
                checked={draft.showOnMobile}
                disabled={readOnly}
                onChange={() => set('showOnMobile', !draft.showOnMobile)}
              />
            </div>

            {problem ? <div className="text-sm text-muted">{problem}</div> : null}
            {saveError ? <Alert>{saveError}</Alert> : null}
            {save.isSuccess ? (
              <div role="status" className="text-success-text text-sm">
                Saved. The shop picks up the change within 5 minutes.
              </div>
            ) : null}
            {readOnly ? null : (
              <Button
                className="self-end"
                disabled={problem !== null || save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            )}
          </Card>

          <div className="flex flex-col gap-4 lg:sticky lg:top-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Segmented
                label="Device"
                value={device}
                onChange={setDevice}
                options={[
                  { value: 'desktop', label: 'Computer' },
                  { value: 'phone', label: 'Phone' },
                ]}
              />
              <Segmented
                label="Preview state"
                value={view}
                onChange={setView}
                options={[
                  { value: 'form', label: 'Form' },
                  { value: 'done', label: 'After signup' },
                ]}
              />
            </div>
            <Preview draft={draft} lang={lang} device={device} view={view} />
            <Card className="p-4 flex flex-col gap-2">
              <h2 className="text-sm font-semibold m-0">Install on the shop</h2>
              {embedCode ? (
                <>
                  <p className="m-0 text-xs text-muted">
                    Paste this one line before the closing body tag in the shop&apos;s theme.
                  </p>
                  <code className="block break-all rounded-[9px] bg-zinc-100 p-2 text-xs">
                    {embedCode}
                  </code>
                  <Button
                    variant="secondary"
                    className="self-start"
                    onClick={() => void copyEmbed()}
                  >
                    {copied ? 'Copied' : 'Copy embed code'}
                  </Button>
                </>
              ) : (
                <p className="m-0 text-xs text-muted">
                  No news domain is set up yet, so there is no script address.
                </p>
              )}
            </Card>
          </div>
        </div>
      ) : null}

      <BrowseTrackingCard />
    </>
  );
}
